import ArrowBack from '@mui/icons-material/ArrowBack';
import ArrowForward from '@mui/icons-material/ArrowForward';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import type React from 'react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

const pompeyoCarrascoZeller = '/landing/caseStudies/pompeyocarrasco.jpg';
const salfaZeller = '/landing/caseStudies/salfa.jpg';

interface CaseStudy {
  id: number;
  company: string;
  date: string;
  title: string;
  description: string;
  category: string;
  readTime: string;
  logo: string;
}

const caseStudies: CaseStudy[] = [
  {
    id: 1,
    company: 'Pompeyo Carrasco',
    date: '22/4/2025',
    title: 'Cómo Pompeyo Carrasco aumentó en un 200% su tasa de cierre.',
    description: 'Cómo Pompeyo Carrasco aumentó en un 200% su tasa de cierre.',
    category: 'Automotriz',
    readTime: '8 min',
    logo: pompeyoCarrascoZeller,
  },
  {
    id: 2,
    company: 'Salfa',
    date: '19/6/2025',
    title: 'Cómo Salfa mejoró su eficiencia y aumentó sus tasas de cierre.',
    description:
      'Una historia de transformación digital exitosa en el sector automotriz a través de procesos automatizados.',
    category: 'Automotriz',
    readTime: '5 min',
    logo: salfaZeller,
  },
];

const CompanyImage = ({ company, logo }: { company: string; logo: string }) => (
  <Box
    sx={{
      width: '100%',
      height: 240,
      background: 'linear-gradient(135deg, #15183A 0%, #1e2147 100%)',
      borderRadius: '16px 16px 0 0',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: 'white',
      fontSize: '20px',
      fontWeight: 'bold',
      position: 'relative',
      overflow: 'hidden',
      '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background:
          'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.03"%3E%3Ccircle cx="30" cy="30" r="2"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
        opacity: 0.3,
      },
    }}
  >
    <Box
      sx={{
        position: 'relative',
        zIndex: 1,
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {logo ? (
        <img
          src={logo}
          alt={company}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            maxWidth: '100%',
            maxHeight: '100%',
          }}
        />
      ) : (
        <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'white' }}>
          {company}
        </Typography>
      )}
    </Box>
  </Box>
);

const SuccessStory = ({ story, onNavigate }: { story: CaseStudy; onNavigate: (id: number) => void }) => (
  <Card
    sx={{
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      borderRadius: 4,
      border: 'none',
      boxShadow: '0 8px 32px rgba(21, 24, 58, 0.08)',
      transition: 'all 0.3s ease-in-out',
      overflow: 'hidden',
      cursor: 'pointer',
      '&:hover': {
        transform: 'translateY(-8px)',
        boxShadow: '0 16px 48px rgba(21, 24, 58, 0.12)',
      },
    }}
    onClick={() => onNavigate(story.id)}
  >
    <CompanyImage company={story.company} logo={story.logo} />

    <CardContent sx={{ p: 4, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <Chip
          label={story.category}
          size="small"
          sx={{
            backgroundColor: 'rgba(249, 208, 10, 0.15)',
            color: '#15183A',
            fontWeight: 'medium',
            fontSize: '11px',
          }}
        />
        <Typography
          variant="caption"
          sx={{
            color: 'text.secondary',
          }}
        >
          {story.readTime} de lectura
        </Typography>
      </Box>

      <Typography
        variant="h6"
        component="h3"
        sx={{
          fontWeight: 'bold',
          lineHeight: 1.3,
          color: '#15183A',
          mb: 2,
          minHeight: '3.6em',
        }}
      >
        {story.title}
      </Typography>

      <Typography
        variant="body2"
        sx={{
          color: 'text.secondary',
          mb: 3,
          lineHeight: 1.6,
          flexGrow: 1,
        }}
      >
        {story.description}
      </Typography>

      <Box sx={{ mt: 'auto' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography
            variant="caption"
            sx={{
              color: 'text.secondary',
            }}
          >
            {story.date}
          </Typography>

          <Button
            endIcon={<ArrowForward />}
            sx={{
              color: '#15183A',
              fontWeight: 'medium',
              fontSize: '14px',
              '&:hover': {
                backgroundColor: 'rgba(21, 24, 58, 0.04)',
              },
            }}
            onClick={(e) => {
              e.stopPropagation();
              onNavigate(story.id);
            }}
          >
            Leer caso
          </Button>
        </Box>
      </Box>
    </CardContent>
  </Card>
);

interface NavigationButtonProps {
  direction: 'prev' | 'next';
  onClick: () => void;
  disabled: boolean;
  size: number;
}

const NavigationButton: React.FC<NavigationButtonProps> = ({ direction, onClick, disabled, size }) => {
  const Icon = direction === 'prev' ? ArrowBack : ArrowForward;
  const fontSize = size === 40 ? 'small' : 'medium';

  return (
    <IconButton
      onClick={onClick}
      disabled={disabled}
      sx={{
        width: size,
        height: size,
        backgroundColor: 'rgba(21, 24, 58, 0.08)',
        color: '#15183A',
        '&:hover': {
          backgroundColor: 'rgba(21, 24, 58, 0.12)',
        },
        '&:disabled': {
          backgroundColor: 'rgba(21, 24, 58, 0.04)',
          color: 'rgba(21, 24, 58, 0.3)',
        },
      }}
    >
      <Icon fontSize={fontSize} />
    </IconButton>
  );
};

const SuccessStories: React.FC<{ caseStudies: CaseStudy[] }> = () => {
  if (!caseStudies) return null;

  const navigate = useNavigate();
  const theme = useTheme();
  const isMdUp = useMediaQuery(theme.breakpoints.up('md'));
  const [currentIndex, setCurrentIndex] = useState(0);

  const storiesPerPage = isMdUp ? 2 : 1;
  const visibleStories = caseStudies.slice(currentIndex, currentIndex + storiesPerPage);

  // Adjust currentIndex when screen size changes to prevent out-of-bounds issues
  useEffect(() => {
    // Calculate the maximum valid index
    const maxIndex = Math.max(0, caseStudies.length - storiesPerPage);

    // If current index would show fewer stories than available or is out of bounds, adjust it
    if (currentIndex > maxIndex) {
      setCurrentIndex(maxIndex);
    }
  }, [storiesPerPage, caseStudies.length, currentIndex]);

  const handleNext = () => {
    if (currentIndex + storiesPerPage < caseStudies.length) {
      setCurrentIndex(currentIndex + storiesPerPage);
    }
  };

  const handlePrev = () => {
    if (currentIndex - storiesPerPage >= 0) {
      setCurrentIndex(currentIndex - storiesPerPage);
    }
  };

  const handleNavigate = (id: number) => {
    window.scrollTo({ top: 0 });
    navigate(`/casos-de-exito/${id}`);
  };

  return (
    <Box sx={{ pt: 6 }}>
      <Container maxWidth="lg">
        {/* Desktop: arrows on sides */}
        <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 3 }}>
          <NavigationButton direction="prev" onClick={handlePrev} disabled={currentIndex === 0} size={40} />

          <Box sx={{ flex: 1 }}>
            <Grid container spacing={4}>
              {visibleStories.map((story) => (
                <Grid size={{ xs: 12, md: 6 }} key={story.id}>
                  <SuccessStory story={story} onNavigate={handleNavigate} />
                </Grid>
              ))}
            </Grid>
          </Box>

          <NavigationButton
            direction="next"
            onClick={handleNext}
            disabled={currentIndex + storiesPerPage >= caseStudies.length}
            size={40}
          />
        </Box>

        {/* Mobile: cards with dots and arrows below */}
        <Box sx={{ display: { xs: 'block', md: 'none' } }}>
          <Grid container spacing={4}>
            {visibleStories.map((story) => (
              <Grid size={12} key={story.id}>
                <SuccessStory story={story} onNavigate={handleNavigate} />
              </Grid>
            ))}
          </Grid>

          {/* Navigation dots and arrows for mobile */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 2,
              mt: 4,
            }}
          >
            <NavigationButton direction="prev" onClick={handlePrev} disabled={currentIndex === 0} size={40} />

            {/* Pagination dots */}
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              {caseStudies.map((story, index) => (
                <Box
                  key={story.id}
                  onClick={() => setCurrentIndex(index)}
                  sx={{
                    width: currentIndex === index ? 10 : 8,
                    height: currentIndex === index ? 10 : 8,
                    borderRadius: '50%',
                    backgroundColor: currentIndex === index ? '#15183A' : 'rgba(21, 24, 58, 0.3)',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      backgroundColor: currentIndex === index ? '#15183A' : 'rgba(21, 24, 58, 0.5)',
                    },
                  }}
                />
              ))}
            </Box>

            <NavigationButton
              direction="next"
              onClick={handleNext}
              disabled={currentIndex + storiesPerPage >= caseStudies.length}
              size={40}
            />
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

const CaseStudyLanding = () => {
  return (
    <Box
      sx={{
        maxWidth: '100%',
        bgcolor: 'backgroundGrey.main',
        py: 6,
      }}
    >
      <Typography
        variant="h2"
        align="center"
        gutterBottom
        sx={{
          color: 'primaryDark.main',
          fontWeight: 'bold',
          fontSize: { xs: '24px', sm: '24px', md: '32px', lg: '40px' },
          mt: 3,
          mb: 3,
        }}
      >
        Lo dicen nuestros clientes
      </Typography>
      <Box sx={{ mt: 3 }}>
        <Typography
          variant="h2"
          align="center"
          gutterBottom
          sx={{
            color: 'primaryDark.main',
            fontWeight: 400,
            fontSize: { xs: '24px', sm: '24px', md: '32px', lg: '40px' },
            my: 3,
          }}
        >
          {/* Y casos de éxito */}
          Con casos de éxito
        </Typography>
        <SuccessStories caseStudies={caseStudies} />
      </Box>
    </Box>
  );
};

export default CaseStudyLanding;
